import { useMemo } from 'react'
import { NoData } from 'src/components/analytics/common/NoData'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { ChartSkeleton } from 'src/components/overview/charts/common/Skeleton'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { formatNumberToTwoDecimals } from 'src/lib/utils/analytics'

type PaymentMethodsConversionRatesData = [string, number][]

export default function PrepaidShareChart() {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.payment_methods_conversion_rates,
      dependencies: {
        endpoints: ['/payment-conversion']
      }
    },
    refreshKey
  })

  const paymentsConversionData = useMemo(() => {
    const rawData: PaymentMethodsConversionRatesData = data[0]?.data?.result?.widgetData?.data
    const comparedData: PaymentMethodsConversionRatesData = data[0]?.data?.result?.widgetData?.compared_data

    if (!rawData || rawData.length === 0) return { data: [] }

    const iterator = (data: PaymentMethodsConversionRatesData) => {
      return data.map(([key, value]) => [key, value ?? 0])
    }
    const rawDataObj: Record<string, number> = Object.fromEntries(iterator(rawData))
    const comparedRawDataObj: Record<string, number> = Object.fromEntries(iterator(comparedData))

    const finalObject = Object.entries(rawDataObj)
      .reduce((acc, [key, value]) => {
        const comparedValue = comparedRawDataObj[key]
        const percentageChange = comparedValue ? value - comparedValue : 0
        acc.push({
          name: key,
          value: formatNumberToTwoDecimals(value),
          comparedValue: formatNumberToTwoDecimals(comparedValue),
          percentageChange: formatNumberToTwoDecimals(percentageChange)
        })
        return acc
      }, [] as { name: string; value: number; comparedValue: number; percentageChange: number }[])
      .sort((a, b) => b.value - a.value)

    return {
      data: finalObject
    }
  }, [data])

  const maxValue = Math.max(...paymentsConversionData.data.map(item => item.value), 100)

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (paymentsConversionData && paymentsConversionData.data.length > 0) {
      return (
        <div className='w-full h-full'>
          <div className='space-y-4 mt-4'>
            {paymentsConversionData.data.map(item => {
              // Calculate bar width (max 100%)
              const barWidth = `${(item.value / maxValue) * 100}%`
              return (
                <div key={item.name} className='flex flex-col space-y-2'>
                  <div className='flex justify-between'>
                    <div className='w-full text-sm'>{item.name}</div>
                    <div className='flex space-x-2'>
                      <div className='text-sm'>{item.value}%</div>
                      <TrendIndicator value={item.percentageChange} formatter={value => `${Math.abs(value)}%`} />
                    </div>
                  </div>
                  <div className='w-full'>
                    <div className='relative h-[2px] bg-gray-100 rounded-full'>
                      <div
                        className='absolute left-0 top-0 h-[2px] bg-green-light rounded-full'
                        style={{ width: barWidth }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full mt-2 pb-4 overflow-y-auto custom-scrollbar'>
      {renderComponent()}
    </div>
  )
}
